import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** The admin action on a non-connected row — also the confirm dialog's title
 *  and the start of the icon button's accessible name. */
export const HIDE_ACCOUNT_LABEL = '从账号池隐藏';

interface HideAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account's display label (`formatAccountLabel`). */
  accountLabel: string;
  /** 「确认隐藏」 — the caller runs `useArchiveAccount` and closes the dialog
   *  when the request settles. */
  onConfirm: () => void;
  /** The request is in flight — both actions are disabled. */
  pending?: boolean;
}

/**
 * Confirm gate in front of hiding a dead / duplicate account from the pool
 * (`POST /accounts/{id}/archive`). The hub change is DB-only, and the dialog
 * says exactly that, so an operator never fears it can cost a number its
 * WhatsApp session: history stays, the gateway is not touched, nothing is
 * logged out or disconnected, and 「已隐藏」 restores it any time.
 *
 * The first focusable (and so the initially focused) control is 「取消」, so a
 * stray Enter never confirms (same as `BannedRetryDialog`).
 */
export function HideAccountDialog({
  open,
  onOpenChange,
  accountLabel,
  onConfirm,
  pending = false,
}: HideAccountDialogProps): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{HIDE_ACCOUNT_LABEL}</DialogTitle>
          <DialogDescription>将 {accountLabel} 从左侧账号池中隐藏。</DialogDescription>
        </DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>只是从账号池隐藏，历史会话和消息全部保留。</li>
          <li>不会碰网关，不会登出或断开任何号。</li>
          <li>随时可以在账号池底部的「已隐藏」里恢复。</li>
        </ul>
        <DialogFooter className="gap-2 sm:gap-2">
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="sm" disabled={pending}>
              取消
            </Button>
          </DialogClose>
          <Button type="button" size="sm" disabled={pending} onClick={onConfirm}>
            确认隐藏
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
